import { type FC, useEffect, useMemo, useRef } from 'react';
import { Dropdown } from '@components/Dropdown';
import { Show, ShowFirstMatching } from '@components/Show';
import { SortToggleButton } from '@components/SortToggleButton';
import { useEntityStore } from '@ContractBuilder/store';
import { useCommentsUIStore } from '@ContractBuilder/store/comments.store';
import type { DbComment } from '@domain/schemas/comment.schema';
import { XIcon } from '@heroicons/react/outline';
import { SortDescendingIcon } from '@heroicons/react/solid';
import { getAllBlocks } from '@root/helpers/blocks';
import { scrollIntoView } from '@root/src/utils/scroll-into-view';
import { ListOrdered } from 'lucide-react';

import { BlockComments } from '../components/BlockComments';
import { EmptyComments } from '../components/EmptyComments';
import { type CommentFilterType, useCommentsFilters } from '../hooks/useCommentsFilters';
import { getCommentBlockId } from '../utils';
import { type CommentThread, groupThreads } from '../utils/threads';

interface CommentsProps {
  data: DbComment[];
  onClose: () => void;
}

export const Comments: FC<CommentsProps> = ({ data, onClose }) => {
  const activeBlockId = useCommentsUIStore(({ activeBlockId }) => activeBlockId);
  const pendingThread = useCommentsUIStore(({ pendingThread }) => pendingThread);
  const submission = useEntityStore(({ submission }) => submission);
  const containerRef = useRef<HTMLDivElement>(null);

  const allBlocks = getAllBlocks(submission);
  const threads = useMemo(() => groupThreads(data), [data]);

  const { filters, setStatusFilter, filteredAndSortedThreads, statusOptions, toggleSortBy } = useCommentsFilters(
    threads,
    allBlocks,
  );

  useEffect(() => {
    if (activeBlockId && containerRef.current) {
      const blockComments = document.getElementById(getCommentBlockId(activeBlockId));
      scrollIntoView(activeBlockId, { block: 'start' });

      if (blockComments && containerRef.current) {
        // Scroll within the container to the block element
        const containerRect = containerRef.current.getBoundingClientRect();
        const blockRect = blockComments.getBoundingClientRect();
        const scrollTop = blockRect.top - containerRect.top + containerRef.current.scrollTop;

        containerRef.current.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        });
      }
    }
  }, [activeBlockId]);

  const showsComposer = Boolean(pendingThread && allBlocks.some((block) => block.id === pendingThread.blockId));
  const isEmpty = filteredAndSortedThreads.length === 0 && !showsComposer;

  const groupedThreads = useMemo(() => {
    const byBlock = new Map<string, CommentThread[]>();
    if (pendingThread) {
      byBlock.set(pendingThread.blockId, []);
    }
    filteredAndSortedThreads.forEach((thread) => {
      byBlock.set(thread.blockId, [...(byBlock.get(thread.blockId) ?? []), thread]);
    });
    return [...byBlock.entries()];
  }, [filteredAndSortedThreads, pendingThread]);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-white">
      <div className="flex items-center justify-between border-b border-gray-200 px-7 py-4">
        <div className="flex flex-col">
          <h2 className="text-base font-normal text-black">Comments</h2>
        </div>
        <XIcon
          className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700"
          data-testid="comments-close-button"
          onClick={onClose}
        />
      </div>

      <div className="flex items-center gap-4 border-b px-7 py-4">
        <Dropdown
          dataTestId="comments-filter-dropdown"
          options={statusOptions}
          value={filters.statusFilter}
          onChange={(value) => setStatusFilter(value as CommentFilterType)}
          className="flex-1"
        />
        <SortToggleButton
          dataTestId="comments-sort-button"
          actionLabel={filters.sortBy === 'newest' ? 'Sort by Block Order' : 'Sort Newest'}
          icon={filters.sortBy === 'newest' ? ListOrdered : SortDescendingIcon}
          onClick={toggleSortBy}
        />
      </div>

      <div className="flex flex-1 flex-col overflow-y-auto" ref={containerRef}>
        <div className="h-px w-full bg-gray-200" />
        <div className="flex h-full flex-1 flex-col">
          <ShowFirstMatching>
            <Show when={isEmpty}>
              <EmptyComments statusFilter={filters.statusFilter} status={submission?.status} />
            </Show>
            <Show when={!isEmpty}>
              {groupedThreads.map(([blockId, blockThreads]) => (
                <BlockComments
                  block={allBlocks.find((block) => block.id === blockId)}
                  isActive={activeBlockId === blockId}
                  key={blockId}
                  threads={blockThreads}
                />
              ))}
            </Show>
          </ShowFirstMatching>
        </div>
      </div>
    </div>
  );
};
